import { Numeric } from "./ValueSwitch/Numeric";
import { CalculationType, useUpdateProperty } from "src/api/client-api/property/useUpdateProperty";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import {
    handleFormulaResponse,
    hasFormulasWithoutScores as checkHasFormulasWithoutScores,
} from "./ValueSwitch/formulaUtils";
import { useQueryClient } from "react-query";
import { getOnePostKey } from "src/api/client-api/post/useGetOnePost";
import { SsqProperty } from "src/api/sure-api/actions/useGetOneAction";
import { Select } from "./ValueSwitch/Select";
import { useMyActionPlanContext } from "../../../../../context/useMyActionPlan";

export type OnUpdate = {
    serializedString: string;
    isGenericValue?: boolean;
    dataListOptionCodes?: string[];
    calculationType?: CalculationType;
};

type ValueSwitchProps = {
    property: SsqProperty;
    value?: string;
    disabled: boolean;
};
const ValueSwitch = (props: ValueSwitchProps) => {
    const { property, value, disabled } = props;
    const { actionPlan, savingStatus, ssqVersion } = useActionsContext();
    const { isEditMode } = useMyActionPlanContext();
    const updateProperty = useUpdateProperty();
    const queryClient = useQueryClient();
    const startingPostId = actionPlan?.startSsqPost.id;

    const updatePropertyValue = (options: OnUpdate) => {
        const { serializedString, isGenericValue, dataListOptionCodes, calculationType } = options;

        const isPropertyMultilevel = !!property.generalizations?.length;

        // Use the SSQ context to check for formulas (this is the correct approach)x
        const isFormulaProperty = checkHasFormulasWithoutScores(ssqVersion, property.id);
        // Determine calculation type
        let finalCalculationType = calculationType;
        if (!finalCalculationType) {
            if (isFormulaProperty) {
                finalCalculationType = "formula";
            } else {
                finalCalculationType = "questionnaire";
            }
        }

        // For formula calculations in Action Plan context, use the starting post ID
        const postIdToUse =
            (finalCalculationType === "formula" || isPropertyMultilevel) && startingPostId ? startingPostId : null;

        if (postIdToUse === null) return;

        savingStatus.setSaving(true);
        updateProperty.mutate(
            {
                postId: postIdToUse, // Use starting post ID for formulas in Action Plan context
                propertyId: property.id,
                value: serializedString || "_empty_",
                position: property.position,
                isGenericValue,
                dataListOptionCodes,
                calculationType: finalCalculationType,
                // For formula calculations in Action Plan, calculate with starting post but update current post
                ...(finalCalculationType === "formula" && startingPostId ?
                    {
                        postToUpdate: postIdToUse,
                    }
                :   {}),
            },
            {
                onSuccess: (response) => {
                    // Handle different response types
                    const formResponse = handleFormulaResponse(
                        response.data,
                        property.id,
                        updateProperty,
                        postIdToUse,
                        queryClient,
                        savingStatus,
                    );

                    if (formResponse.isFormula) {
                        // Don't clear loading state here - let the target property update handle it
                    } else {
                        // For non-formula responses, clear loading state normally
                        savingStatus.setSaving(false);
                    }

                    queryClient.invalidateQueries([getOnePostKey, postIdToUse]);
                },
                onError: (error) => {
                    savingStatus.setSaving(false);
                },
                onSettled: () => {
                    // Only clear loading state if it's not a formula calculation
                    // Formula calculations will be handled by the target property update
                },
            },
        );
    };

    const commonProps = {
        property,
        updatePropertyValue,
        isLocked: savingStatus.isSaving || isEditMode,
        value,
        disabled,
    };

    if (property?.field?.type === "number") {
        return <Numeric {...commonProps} />;
    }

    // TODO: [ds] property builder for Action Plan was ditched so the fields have to be readjusted
    // if (property?.field?.type === "text") {
    //     return <Text {...commonProps} />;
    // }

    // if (property?.field?.type === "checkbox") {
    //     return <Checkbox {...commonProps} />;
    // }

    // if (property?.field?.type === "radio") {
    //     return <Radio {...commonProps} />;
    // }

    if (property?.field?.type === "select") {
        return <Select {...commonProps} />;
    }
    return "Field type not handled";
};

type ActionValuePropertyProps = {
    property: SsqProperty;
    value?: string;
    disabled?: boolean;
};
export const ActionValueProperty = (props: ActionValuePropertyProps) => {
    const { property, value, disabled = false } = props;

    return <ValueSwitch property={property} value={value} disabled={disabled} />;
};
